import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { message } from "antd";
import { sortBy } from "lodash";
import {
  getDicByObjectType,
  getDictByTypeRoute,
  postDictionaryRoute,
  deleteTerminVersionRoute,
  fetchDictionaryRoute,
  fetchDictionaryTypesRoute,
  fetchTerminVersionsRoute,
  updateTerminVersionRoute,
} from "../../lib/routes/dictionaries";
import { DictionaryDto } from "../../lib/interfaces/DictionaryDto";
import { LoadingStatusCode } from "../../lib/types/LoadingStatusCode";
import { sortByToDate } from "../../lib/utils/sortByToDate";
import loadingStatusCodes from "../../lib/const/loadingStatusCodes";
import { PhoneType } from "../../lib/interfaces/PhoneType";
import { DictionaryTypeAlias } from "../../lib/types/DictionaryTypeAlias";
import { DictionaryType } from "../../lib/types/DictionaryType";
import { catchApiError } from "../../lib/utils/catchApiError";
import { AnyObject } from "../../lib/interfaces/AnyObject";
import { Property } from "../../lib/interfaces";
import { getDicVersionsRoute } from "../../lib/routes/properties";
import { OFFER_STATUSES } from "./offering/const";
import { DicTypes } from "../../lib/const/dicTypes";

interface InitialState {
  accountStatuses: DictionaryDto[];
  accountStatusesLoading: LoadingStatusCode;
  countries: DictionaryDto[];
  countriesLoading: LoadingStatusCode;
  regions: DictionaryDto[];
  regionsLoading: LoadingStatusCode;
  docTypes: DictionaryDto[];
  docTypesLoading: LoadingStatusCode;
  countriesTypes: DictionaryDto[];
  countriesTypesLoading: LoadingStatusCode;
  notificationEventTypes: DictionaryDto[];
  notificationEventTypesLoading: LoadingStatusCode;
  accountListRowDicData: {
    organizationDicVersions: Property[];
    organizationDicVersionsLoading: LoadingStatusCode;
  };
  batchStatus: DictionaryDto[];
  batchStatusLoading: LoadingStatusCode;
  phoneTypes: PhoneType[];
  phoneTypesLoading: LoadingStatusCode;
  dictionaryTypes: DictionaryType[];
  dictionaryTypesLoading: LoadingStatusCode;
  activeDictionary: DictionaryDto[];
  activeDictionaryLoading: LoadingStatusCode;
  parentDictionary: DictionaryDto[];
  parentDictionaryLoading: LoadingStatusCode;
  terminVersions: DictionaryDto[];
  terminVersionsLoading: LoadingStatusCode;
  maxTeminId: number;
  maxTeminIdLoading: LoadingStatusCode;
  offeringStatuses: DictionaryDto<OFFER_STATUSES>[];
  offeringStatusesLoading: LoadingStatusCode;
  offeringAccessTypes: DictionaryDto[];
  offeringAccessTypesLoading: LoadingStatusCode;
  offeringProlongTypes: DictionaryDto[];
  offeringProlongTypesLoading: LoadingStatusCode;
  offeringBundleTypes: DictionaryDto[];
  offeringBundleTypesLoading: LoadingStatusCode;
  lifespanPeriods: DictionaryDto[];
  lifespanPeriodsLoading: LoadingStatusCode;
  destinationTypes: DictionaryDto[];
  destinationTypesLoading: LoadingStatusCode;
  destinationCategories: DictionaryDto[];
  destinationCategoriesLoading: LoadingStatusCode;
  serviceUsageProcessingType: DictionaryDto[];
  serviceUsageProcessingTypeLoading: LoadingStatusCode;
  billPeriods: DictionaryDto[];
  billPeriodsLoading: LoadingStatusCode;
  reportType: DictionaryDto[];
  reportTypeLoading: LoadingStatusCode;
  accountTypes: DictionaryDto[];
  accountTypesLoading: LoadingStatusCode;
  paymentGoalTypes: DictionaryDto[];
  paymentGoalTypesLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  accountStatuses: [],
  accountStatusesLoading: loadingStatusCodes.idle,
  countries: [],
  countriesLoading: loadingStatusCodes.idle,
  regions: [],
  regionsLoading: loadingStatusCodes.idle,
  docTypes: [],
  docTypesLoading: loadingStatusCodes.idle,
  countriesTypes: [],
  countriesTypesLoading: loadingStatusCodes.idle,
  notificationEventTypes: [],
  notificationEventTypesLoading: loadingStatusCodes.idle,
  accountListRowDicData: {
    organizationDicVersions: [],
    organizationDicVersionsLoading: loadingStatusCodes.idle,
  },
  batchStatus: [],
  batchStatusLoading: loadingStatusCodes.idle,
  phoneTypes: [],
  phoneTypesLoading: loadingStatusCodes.idle,
  dictionaryTypes: [],
  dictionaryTypesLoading: "idle",
  activeDictionary: [],
  activeDictionaryLoading: "idle",
  parentDictionary: [],
  parentDictionaryLoading: "idle",
  terminVersions: [],
  terminVersionsLoading: "idle",
  maxTeminId: 0,
  maxTeminIdLoading: "idle",
  offeringStatuses: [],
  offeringStatusesLoading: "idle",
  offeringAccessTypes: [],
  offeringAccessTypesLoading: "idle",
  offeringProlongTypes: [],
  offeringProlongTypesLoading: "idle",
  offeringBundleTypes: [],
  offeringBundleTypesLoading: "idle",
  lifespanPeriods: [],
  lifespanPeriodsLoading: "idle",
  destinationTypes: [],
  destinationTypesLoading: "idle",
  destinationCategories: [],
  destinationCategoriesLoading: "idle",
  serviceUsageProcessingType: [],
  serviceUsageProcessingTypeLoading: "idle",
  billPeriods: [],
  billPeriodsLoading: "idle",
  reportType: [],
  reportTypeLoading: loadingStatusCodes.idle,
  accountTypes: [],
  accountTypesLoading: loadingStatusCodes.idle,
  paymentGoalTypes: [],
  paymentGoalTypesLoading: loadingStatusCodes.idle,
};

const fetchAccountStatuses = createAsyncThunk("dictionaries/fetchAccountStatuses", async () => {
  const response = axios.get(getDictByTypeRoute("acc_status"));
  const { data } = await response;
  return data;
});

const fetchAccountTypes = createAsyncThunk("dictionaries/fetchAccountTypes", async () => {
  const response = axios.get(getDictByTypeRoute("acc_type"));
  const { data } = await response;
  return data;
});

const fetchCountries = createAsyncThunk("dictionaries/fetchCountries", async () => {
  const response = axios.get(getDictByTypeRoute("country"));
  const { data } = await response;
  return data;
});

const fetchRegions = createAsyncThunk("dictionaries/fetchRegions", async () => {
  const response = axios.get(getDictByTypeRoute("region"));
  const { data } = await response;
  return data;
});

const fetchPhoneTypes = createAsyncThunk("dictionaries/fetchPhoneTypes", async () => {
  const response = axios.get(getDictByTypeRoute("phone_type"));
  const { data } = await response;
  return data;
});

const getNotificationEventTypes = createAsyncThunk(
  "dictionaries/getNotificationEventTypes",
  async () => {
    const response = axios.get(getDicByObjectType("notification_event_type"));
    const { data } = await response;
    return data;
  }
);

const getCountriesTypes = createAsyncThunk("dictionaries/getCountriesTypes", async () => {
  const response = axios.get(getDictByTypeRoute("country"));
  const { data } = await response;
  return data;
});

const getDocTypes = createAsyncThunk("dictionaries/getDocTypes", async () => {
  const response = axios.get(getDictByTypeRoute("id_doc_type"));
  const { data } = await response;
  return data;
});

const getOrganizationDicVersions = createAsyncThunk(
  "dictionaries/getOrganizationDicVersions",
  async ({ organizationId }: { organizationId: number }) => {
    const response = axios.get(
      getDicVersionsRoute({ objectTypeSysname: "organization", objectId: organizationId })
    );
    const { data } = await response;
    return data;
  }
);

export const fetchBatchStatusDictionary = createAsyncThunk(
  "dictionaries/fetchBatchStatusDictionary",
  async () => {
    const response = await axios.get(getDictByTypeRoute("pl_batch_status"));
    const { data } = response;
    return data;
  }
);

export const fetchAccTypes = createAsyncThunk("dictionaries/fetchAccTypes", async () => {
  const response = await axios.get(getDictByTypeRoute("acc_type"));
  const { data } = response;
  return data;
});

export const fetchDestinationTypes = createAsyncThunk(
  "dictionaries/fetchDestinationTypes",
  async () => {
    try {
      const response = axios.get(getDictByTypeRoute("destination_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки типов направлений. Попробуйте позже",
      });
    }
  }
);

export const fetchDestinationCategories = createAsyncThunk(
  "dictionaries/fetchDestinationCategories",
  async () => {
    try {
      const response = axios.get(getDictByTypeRoute("destination_category"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки категорий направлений. Попробуйте позже",
      });
    }
  }
);

// Получение списка словарей
export const fetchDictionaryTypes = createAsyncThunk(
  "dictionaries/fetchDictionaryTypes",
  async () => {
    try {
      const response = axios.get(fetchDictionaryTypesRoute());
      const { data } = await response;
      return [...data].sort((a, b) => (a.name > b.name ? 1 : -1));
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
      });
    }
  }
);

// Получение конкретного словаря
export const fetchDictionary = createAsyncThunk<
  DictionaryDto[],
  {
    dicTypeAlias: DictionaryTypeAlias;
    includeClosed: boolean;
    onDate: string;
  }
>("dictionaries/fetchDictionary", async (params) => {
  try {
    const response = axios.get<DictionaryDto[]>(fetchDictionaryRoute(params));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
    });
  }
});

// Получение родительского словаря
export const fetchParentDictionary = createAsyncThunk<
  DictionaryDto[],
  {
    dicTypeAlias: DictionaryTypeAlias;
  }
>("dictionaries/fetchParentDictionary", async ({ dicTypeAlias }) => {
  try {
    const response = axios.get(fetchDictionaryRoute({ dicTypeAlias }));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
    });
  }
});

// Получение версий словарного термина
export const fetchTerminVersions = createAsyncThunk<
  DictionaryDto[],
  { dicTypeAlias: DictionaryTypeAlias; id: number }
>("dictionaries/fetchTerminVersions", async (props) => {
  try {
    const response = axios.get(fetchTerminVersionsRoute(props));
    const { data } = await response;
    return data.sort(sortByToDate);
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
    });
  }
});

// Закрытие версии термина
export const deleteTerminVersion = createAsyncThunk<
  Promise<any>,
  {
    id: number;
    dicTypeAlias: DictionaryTypeAlias;
    fromDate: string;
  }
>("dictionaries/deleteTerminVersion", async (params) => {
  try {
    const url = deleteTerminVersionRoute(params);
    const response = axios.delete(url);
    const { data } = await response;
    message.success("Дата успешно изменена");
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
    });
  }
});

// Обновление версии термина
export const updateTerminVersion = createAsyncThunk(
  "dictionaries/updateTerminVersion",
  // TODO типизировать values
  async ({ fromDate, values }: { fromDate: string; values: AnyObject }) => {
    try {
      const response = axios.put(updateTerminVersionRoute(fromDate), values);
      const { data } = await response;
      message.success("Версия успешно добавлена");
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
      });
    }
  }
);

// Получение макс. id, необходимо передавать при создании термина
export const fetchMaxTeminId = createAsyncThunk(
  "dictionaries/fetchMaxTeminId",
  async ({ dicTypeAlias }: { dicTypeAlias: DictionaryTypeAlias }) => {
    try {
      const response = axios.get(fetchDictionaryRoute({ dicTypeAlias, includeClosed: true }));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
      });
    }
  }
);

// Создание термина
export const createDicTermin = createAsyncThunk(
  "dictionaries/createDicTermin",
  async (newTermin: Partial<DictionaryDto>) => {
    try {
      const response = axios.post(postDictionaryRoute(), newTermin);
      const { data } = await response;
      message.success("Термин успешно создан");
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
      });
    }
  }
);

// Статусы предложения
export const fetchOfferingStatuses = createAsyncThunk(
  "dictionaries/fetchOfferingStatuses",
  async () => {
    try {
      const response = axios.get(getDictByTypeRoute("offering_status"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
      });
    }
  }
);

// Типы предложений
export const fetchOfferingAccessTypes = createAsyncThunk(
  "dictionaries/fetchOfferingAccessTypes",
  async () => {
    try {
      const response = axios.get(getDictByTypeRoute("offering_access_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
      });
    }
  }
);

// Типы пролонгации
export const fetchOfferingProlongTypes = createAsyncThunk(
  "dictionaries/fetchOfferingProlongTypes",
  async () => {
    try {
      const response = axios.get(getDictByTypeRoute("offering_prolong_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
      });
    }
  }
);

// Типы группировки
export const fetchBundleTypes = createAsyncThunk("dictionaries/fetchBundleTypes", async () => {
  try {
    const response = axios.get(getDictByTypeRoute("bundle_type"));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
    });
  }
});

// Период продукта
export const fetchLifespanPeriods = createAsyncThunk(
  "dictionaries/fetchLifespanPeriods",
  async () => {
    try {
      const response = axios.get(getDictByTypeRoute("lifespan_period"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
      });
    }
  }
);

// Периодичность счета на постоплату
export const fetchBillPeriods = createAsyncThunk("dictionaries/fetchBillPeriods", async () => {
  try {
    const response = axios.get(getDictByTypeRoute("bill_period"));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
    });
  }
});

// Типы целей платежа
export const fetchPaymentGoalTypes = createAsyncThunk<DictionaryDto[]>(
  "dictionaries/fetchPaymentGoalTypes",
  async () => {
    try {
      const response = axios.get(getDictByTypeRoute(DicTypes.PaymentGoalType));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
      });
    }
  }
);

export const fetchServiceUsageProcessingType = createAsyncThunk(
  "dictionaries/fetchServiceUsageProcessingType",
  async () => {
    const response = await axios.get(getDictByTypeRoute("service_usage_processing_type"));
    const { data } = response;
    return data;
  }
);

export const fetchReportType = createAsyncThunk("dictionaries/fetchReportType", async () => {
  const response = await axios.get(getDictByTypeRoute("report_type"));
  const { data } = response;
  return data;
});

export const dictSlice = createSlice({
  name: "serviceTypes",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountStatuses.fulfilled, (state, action) => {
        state.accountStatuses = action.payload;
        state.accountStatusesLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchAccountStatuses.pending, (state) => {
        state.accountStatusesLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchAccountStatuses.rejected, (state) => {
        state.accountStatusesLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchAccountTypes.fulfilled, (state, action) => {
        state.accountTypes = action.payload;
        state.accountTypesLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchAccountTypes.pending, (state) => {
        state.accountTypesLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchAccountTypes.rejected, (state) => {
        state.accountTypesLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.countries = [...action.payload.sort((a, b) => (a.name > b.name ? 1 : -1))];
        state.countriesLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchCountries.pending, (state) => {
        state.countriesLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchCountries.rejected, (state) => {
        state.countriesLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchRegions.fulfilled, (state, action) => {
        state.regions = [...action.payload.sort((a, b) => (a.name > b.name ? 1 : -1))];
        state.regionsLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchRegions.pending, (state) => {
        state.regionsLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchRegions.rejected, (state) => {
        state.regionsLoading = loadingStatusCodes.rejected;
      })

      .addCase(getNotificationEventTypes.fulfilled, (state, action) => {
        state.notificationEventTypes = action.payload;
        state.notificationEventTypesLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(getNotificationEventTypes.pending, (state) => {
        state.notificationEventTypesLoading = loadingStatusCodes.pending;
      })
      .addCase(getNotificationEventTypes.rejected, (state) => {
        state.notificationEventTypesLoading = loadingStatusCodes.rejected;
      })
      .addCase(getDocTypes.fulfilled, (state, action) => {
        state.docTypes = action.payload;
        state.docTypesLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(getDocTypes.pending, (state) => {
        state.docTypesLoading = loadingStatusCodes.pending;
      })
      .addCase(getDocTypes.rejected, (state) => {
        state.docTypesLoading = loadingStatusCodes.rejected;
      })
      .addCase(getOrganizationDicVersions.fulfilled, (state, action) => {
        state.accountListRowDicData.organizationDicVersions = action.payload;
        state.accountListRowDicData.organizationDicVersionsLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(getOrganizationDicVersions.pending, (state) => {
        state.accountListRowDicData.organizationDicVersionsLoading = loadingStatusCodes.pending;
      })
      .addCase(getOrganizationDicVersions.rejected, (state) => {
        state.accountListRowDicData.organizationDicVersionsLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchBatchStatusDictionary.pending, (state) => {
        state.batchStatusLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchBatchStatusDictionary.fulfilled, (state, action) => {
        state.batchStatusLoading = loadingStatusCodes.fulfilled;
        state.batchStatus = [...action.payload.sort((a, b) => (a.id > b.id ? 1 : -1))];
      })
      .addCase(fetchBatchStatusDictionary.rejected, (state) => {
        state.batchStatusLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchPhoneTypes.pending, (state) => {
        state.phoneTypesLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchPhoneTypes.rejected, (state) => {
        state.phoneTypesLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchPhoneTypes.fulfilled, (state, { payload }) => {
        state.phoneTypesLoading = loadingStatusCodes.fulfilled;
        state.phoneTypes = [...payload].sort((a, b) => a.id - b.id);
      })
      .addCase(getCountriesTypes.fulfilled, (state, action) => {
        state.countriesTypes = action.payload;
        state.countriesTypesLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(getCountriesTypes.pending, (state) => {
        state.countriesTypesLoading = loadingStatusCodes.pending;
      })
      .addCase(getCountriesTypes.rejected, (state) => {
        state.countriesTypesLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchDictionaryTypes.pending, (state) => {
        state.dictionaryTypesLoading = "pending";
      })
      .addCase(fetchDictionaryTypes.rejected, (state) => {
        state.dictionaryTypesLoading = "rejected";
        state.dictionaryTypes = [];
      })
      .addCase(fetchDictionaryTypes.fulfilled, (state, { payload }) => {
        state.dictionaryTypesLoading = "fulfilled";
        state.dictionaryTypes = payload;
      })
      .addCase(fetchDictionary.pending, (state) => {
        state.activeDictionaryLoading = "pending";
      })
      .addCase(fetchDictionary.rejected, (state) => {
        state.activeDictionaryLoading = "rejected";
        state.activeDictionary = [];
      })
      .addCase(fetchDictionary.fulfilled, (state, { payload }) => {
        state.activeDictionaryLoading = "fulfilled";
        state.activeDictionary = payload;
      })
      .addCase(fetchParentDictionary.pending, (state) => {
        state.parentDictionaryLoading = "pending";
      })
      .addCase(fetchParentDictionary.rejected, (state) => {
        state.parentDictionaryLoading = "rejected";
        state.parentDictionary = [];
      })
      .addCase(fetchParentDictionary.fulfilled, (state, { payload }) => {
        state.parentDictionaryLoading = "fulfilled";
        state.parentDictionary = payload;
      })
      .addCase(fetchTerminVersions.pending, (state) => {
        state.terminVersionsLoading = "pending";
      })
      .addCase(fetchTerminVersions.rejected, (state) => {
        state.terminVersionsLoading = "rejected";
        state.terminVersions = [];
      })
      .addCase(fetchTerminVersions.fulfilled, (state, { payload }) => {
        state.terminVersionsLoading = "fulfilled";
        state.terminVersions = payload;
      })
      .addCase(fetchMaxTeminId.pending, (state) => {
        state.maxTeminIdLoading = "pending";
      })
      .addCase(fetchMaxTeminId.rejected, (state) => {
        state.maxTeminIdLoading = "rejected";
        state.maxTeminId = 0;
      })
      .addCase(fetchMaxTeminId.fulfilled, (state, { payload }) => {
        state.maxTeminIdLoading = "fulfilled";
        state.maxTeminId = Math.max(...payload.map(({ id }) => id));
      })
      .addCase(fetchOfferingStatuses.pending, (state) => {
        state.offeringStatusesLoading = "pending";
      })
      .addCase(fetchOfferingStatuses.rejected, (state) => {
        state.offeringStatusesLoading = "rejected";
        state.offeringStatuses = [];
      })
      .addCase(fetchOfferingStatuses.fulfilled, (state, { payload }) => {
        state.offeringStatusesLoading = "fulfilled";
        state.offeringStatuses = payload;
      })
      .addCase(fetchOfferingAccessTypes.pending, (state) => {
        state.offeringAccessTypesLoading = "pending";
      })
      .addCase(fetchOfferingAccessTypes.rejected, (state) => {
        state.offeringAccessTypesLoading = "rejected";
        state.offeringAccessTypes = [];
      })
      .addCase(fetchOfferingAccessTypes.fulfilled, (state, { payload }) => {
        state.offeringAccessTypesLoading = "fulfilled";
        state.offeringAccessTypes = payload;
      })
      .addCase(fetchOfferingProlongTypes.pending, (state) => {
        state.offeringProlongTypesLoading = "pending";
      })
      .addCase(fetchOfferingProlongTypes.rejected, (state) => {
        state.offeringProlongTypesLoading = "rejected";
        state.offeringProlongTypes = [];
      })
      .addCase(fetchOfferingProlongTypes.fulfilled, (state, { payload }) => {
        state.offeringProlongTypesLoading = "fulfilled";
        state.offeringProlongTypes = payload;
      })
      .addCase(fetchBundleTypes.pending, (state) => {
        state.offeringBundleTypesLoading = "pending";
      })
      .addCase(fetchBundleTypes.rejected, (state) => {
        state.offeringBundleTypesLoading = "rejected";
        state.offeringBundleTypes = [];
      })
      .addCase(fetchBundleTypes.fulfilled, (state, { payload }) => {
        state.offeringBundleTypesLoading = "fulfilled";
        state.offeringBundleTypes = payload;
      })
      .addCase(fetchLifespanPeriods.pending, (state) => {
        state.lifespanPeriodsLoading = "pending";
      })
      .addCase(fetchLifespanPeriods.rejected, (state) => {
        state.lifespanPeriodsLoading = "rejected";
        state.lifespanPeriods = [];
      })
      .addCase(fetchLifespanPeriods.fulfilled, (state, { payload }) => {
        state.lifespanPeriodsLoading = "fulfilled";
        state.lifespanPeriods = payload;
      })
      .addCase(fetchDestinationTypes.pending, (state) => {
        state.destinationTypesLoading = "pending";
      })
      .addCase(fetchDestinationTypes.rejected, (state) => {
        state.destinationTypesLoading = "rejected";
        state.destinationTypes = [];
      })
      .addCase(fetchDestinationTypes.fulfilled, (state, { payload }) => {
        state.destinationTypesLoading = "fulfilled";
        state.destinationTypes = payload;
      })
      .addCase(fetchDestinationCategories.pending, (state) => {
        state.destinationCategoriesLoading = "pending";
      })
      .addCase(fetchDestinationCategories.rejected, (state) => {
        state.destinationCategoriesLoading = "rejected";
        state.destinationCategories = [];
      })
      .addCase(fetchDestinationCategories.fulfilled, (state, { payload }) => {
        state.destinationCategoriesLoading = "fulfilled";
        state.destinationCategories = payload;
      })
      .addCase(fetchServiceUsageProcessingType.fulfilled, (state, action) => {
        const sortedData = [...action.payload].sort((a, b) => (a.id > b.id ? 1 : -1));
        state.serviceUsageProcessingType = sortedData;
        state.serviceUsageProcessingTypeLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchServiceUsageProcessingType.pending, (state) => {
        state.serviceUsageProcessingTypeLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchServiceUsageProcessingType.rejected, (state) => {
        state.serviceUsageProcessingTypeLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchBillPeriods.pending, (state) => {
        state.billPeriodsLoading = "pending";
      })
      .addCase(fetchBillPeriods.rejected, (state) => {
        state.billPeriodsLoading = "rejected";
        state.billPeriods = [];
      })
      .addCase(fetchBillPeriods.fulfilled, (state, { payload }) => {
        state.billPeriodsLoading = "fulfilled";
        state.billPeriods = payload;
      })
      .addCase(fetchReportType.fulfilled, (state, action) => {
        state.reportType = action.payload;
        state.reportTypeLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchReportType.pending, (state) => {
        state.reportTypeLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchReportType.rejected, (state) => {
        state.reportTypeLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchPaymentGoalTypes.pending, (state) => {
        state.paymentGoalTypesLoading = "pending";
      })
      .addCase(fetchPaymentGoalTypes.rejected, (state) => {
        state.paymentGoalTypesLoading = "rejected";
        state.paymentGoalTypes = [];
      })
      .addCase(fetchPaymentGoalTypes.fulfilled, (state, { payload }) => {
        state.paymentGoalTypesLoading = "fulfilled";
        state.paymentGoalTypes = sortBy(payload, "id");
      });
  },
});

const { reducer } = dictSlice;

export {
  reducer,
  fetchAccountStatuses,
  fetchCountries,
  fetchRegions,
  getNotificationEventTypes,
  getDocTypes,
  getOrganizationDicVersions,
  fetchPhoneTypes,
  getCountriesTypes,
  fetchAccountTypes,
};
